/**
 * Tava homepage: skin quiz (progress bar + recommendation), carousel, scroll fade-in.
 * Idempotent binds per DOM node — safe across theme editor section reloads.
 */
(function () {
  /**
   * Updates quiz progress bar width and visibility.
   * @param {HTMLElement} container - [data-tava-skin-quiz] wrapper
   * @param {number} stepIndex - Zero-based panel index while questions active
   * @param {number} totalSteps - Question count (typically 4)
   * @param {boolean} showBar - Hide bar on result screens
   */
  function setQuizProgress(container, stepIndex, totalSteps, showBar) {
    var wrap = container.querySelector('[data-quiz-progress-wrap]');
    var fill = container.querySelector('[data-quiz-progress-fill]');
    if (wrap) {
      wrap.classList.toggle('is-hidden', showBar === false);
      wrap.setAttribute('aria-hidden', showBar ? 'false' : 'true');
    }
    if (fill && showBar !== false && totalSteps > 0) {
      var pct = ((stepIndex + 1) / totalSteps) * 100;
      fill.style.width = pct + '%';
    }
  }

  /**
   * Parses quiz JSON, wires radios, CTAs, and progress meter.
   * @param {HTMLElement} container
   */
  function initSkinQuiz(container) {
    if (container.dataset.tavaQuizBound === 'true') return;
    container.dataset.tavaQuizBound = 'true';

    var jsonEl = container.querySelector('[data-tava-quiz-config]');
    if (!jsonEl || !jsonEl.textContent.trim()) return;

    /** @type {{ radiantUrl?: string; ghkUrl?: string }} */
    var config = {};
    try {
      config = JSON.parse(jsonEl.textContent);
    } catch (e) {
      return;
    }

    var radiantUrl = config.radiantUrl || '';
    var ghkUrl = config.ghkUrl || '';

    var panels = container.querySelectorAll('[data-quiz-panel]');
    var resultPanel = container.querySelector('[data-quiz-result]');
    var prevBtn = container.querySelector('[data-quiz-prev]');
    var nextBtn = container.querySelector('[data-quiz-next]');
    var stepLabel = container.querySelector('[data-quiz-step]');

    var currentStep = 0;
    var totalSteps = panels.length;

    /** @returns {{ radiant: number; ghk: number }} */
    function scoreFromAnswers() {
      var radiant = 0;
      var ghk = 0;
      for (var i = 0; i < panels.length; i++) {
        var radio = panels[i].querySelector('input[type="radio"]:checked');
        if (!radio) continue;
        radiant += Number(radio.getAttribute('data-weight-radiant') || 0);
        ghk += Number(radio.getAttribute('data-weight-ghk') || 0);
      }
      return { radiant: radiant, ghk: ghk };
    }

    function showStep(index) {
      for (var i = 0; i < panels.length; i++) {
        panels[i].hidden = i !== index;
      }
      if (stepLabel) stepLabel.textContent = 'Question ' + (index + 1) + ' of ' + totalSteps;
      if (prevBtn) prevBtn.hidden = index === 0;
      if (nextBtn) {
        nextBtn.hidden = false;
        nextBtn.textContent = index === totalSteps - 1 ? 'See recommendation' : 'Next';
      }
      setQuizProgress(container, index, totalSteps, true);
    }

    /** @returns {{ product: string; heading: string }} */
    function recommendation() {
      var scores = scoreFromAnswers();
      var pickGhk = scores.ghk >= scores.radiant;
      if (pickGhk) {
        return {
          product: 'ghk',
          heading:
            'Based on your answers, we recommend starting with our premium GHK-CU Honey Tallow Balm for the most complete skin support.',
        };
      }
      return {
        product: 'radiant',
        heading:
          'Based on your answers, our Volufiline + Matrixyl Peptide Plumping Cream is a beautiful match for visible plumpness and smoother-looking skin.',
      };
    }

    function showResult() {
      var rec = recommendation();
      var headingEl = container.querySelector('[data-quiz-result-heading]');
      var ctaEl = container.querySelector('[data-quiz-result-cta]');

      for (var i = 0; i < panels.length; i++) {
        panels[i].hidden = true;
      }
      if (resultPanel) resultPanel.hidden = false;
      if (nextBtn) nextBtn.hidden = true;
      if (prevBtn) prevBtn.hidden = false;
      if (stepLabel) stepLabel.textContent = 'Your match';

      setQuizProgress(container, 0, totalSteps, false);

      if (headingEl) headingEl.textContent = rec.heading;

      if (ctaEl) {
        var url = rec.product === 'ghk' ? ghkUrl : radiantUrl;
        if (url) {
          ctaEl.href = url;
          ctaEl.hidden = false;
          ctaEl.textContent =
            rec.product === 'ghk' ? 'Shop premium GHK-CU' : 'Shop Plumping Cream';
        } else {
          ctaEl.hidden = true;
        }
      }
    }

    /** @returns {HTMLInputElement | null} */
    function checkedInPanel(panelIndex) {
      var panel = panels[panelIndex];
      var checked = panel.querySelector('input[type="radio"]:checked');
      return /** @type {HTMLInputElement | null} */ (checked);
    }

    function validateCurrent() {
      return !!checkedInPanel(currentStep);
    }

    nextBtn?.addEventListener('click', function () {
      if (!validateCurrent()) return;

      if (currentStep === totalSteps - 1) {
        showResult();
        return;
      }
      currentStep += 1;
      showStep(currentStep);
    });

    prevBtn?.addEventListener('click', function () {
      if (resultPanel && !resultPanel.hidden) {
        resultPanel.hidden = true;
        currentStep = totalSteps - 1;
        showStep(currentStep);
        return;
      }
      if (currentStep <= 0) return;
      currentStep -= 1;
      showStep(currentStep);
    });

    showStep(0);
  }

  /**
   * Carousel prev/next arrows for testimonial viewport.
   * @param {HTMLElement} viewport
   */
  function initTestimonialCarousel(viewport) {
    if (viewport.dataset.tavaCarouselBound === 'true') return;
    viewport.dataset.tavaCarouselBound = 'true';

    var prev = viewport.parentElement?.querySelector('[data-tava-testimonials-prev]');
    var next = viewport.parentElement?.querySelector('[data-tava-testimonials-next]');
    function slideEl() {
      return viewport.querySelector('.tava-testimonial');
    }
    function scroll(amount) {
      var slide = slideEl();
      var scrollAmount = slide ? slide.offsetWidth + 24 : viewport.clientWidth * 0.85;
      viewport.scrollBy({ left: amount * scrollAmount, behavior: 'smooth' });
    }
    prev?.addEventListener('click', function () {
      scroll(-1);
    });
    next?.addEventListener('click', function () {
      scroll(1);
    });
  }

  /**
   * Fade-in .tava-polish-reveal elements when they enter the viewport.
   */
  function initScrollReveal() {
    var nodes = document.querySelectorAll('.tava-polish-reveal:not([data-tava-reveal-bound])');
    if (!nodes.length) return;

    if (!('IntersectionObserver' in window)) {
      nodes.forEach(function (el) {
        el.classList.add('is-visible');
        el.setAttribute('data-tava-reveal-bound', '1');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -6% 0px' }
    );

    nodes.forEach(function (el) {
      el.setAttribute('data-tava-reveal-bound', '1');
      observer.observe(el);
    });
  }

  function boot() {
    document.querySelectorAll('[data-tava-skin-quiz]').forEach(function (el) {
      initSkinQuiz(/** @type {HTMLElement} */ (el));
    });
    document.querySelectorAll('[data-tava-testimonials-viewport]').forEach(function (viewport) {
      initTestimonialCarousel(/** @type {HTMLElement} */ (viewport));
    });
    initScrollReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
